import { useState, useEffect, useCallback } from "react";
import {
  type User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
  updateProfile,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "@/lib/firebase";
import {
  parseFeatureNotInPlan,
  type FeatureNotInPlanDetails,
} from "@/lib/featureNotInPlan";

const BACKEND_URL =
  import.meta.env.VITE_BACKEND_URL || "https://localhost:4001";
const BACKEND_FETCH_TIMEOUT_MS = 10_000;
const LEGALIX_CHANNEL_WEBAPP = "webapp";
const SIGNUP_PHONE_STORAGE_KEY = "legalix_webapp_signup_phone";
const REGISTER_PHONE_RE = /^[\d\s\-+()]+$/;

export const stashSignupPhone = (phone: string) => {
  const trimmed = phone.trim();
  try {
    if (trimmed) sessionStorage.setItem(SIGNUP_PHONE_STORAGE_KEY, trimmed);
    else sessionStorage.removeItem(SIGNUP_PHONE_STORAGE_KEY);
  } catch {
    /* ignore quota / private mode */
  }
};

export const readSignupPhone = (): string => {
  try {
    return sessionStorage.getItem(SIGNUP_PHONE_STORAGE_KEY)?.trim() || "";
  } catch {
    return "";
  }
};

export const clearSignupPhone = () => {
  try {
    sessionStorage.removeItem(SIGNUP_PHONE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

export const isValidSignupPhone = (phone: string): boolean =>
  !!phone.trim() && REGISTER_PHONE_RE.test(phone.trim());

const isPhoneRequiredError = (body: unknown): boolean => {
  if (!body || typeof body !== "object") return false;
  const record = body as { code?: unknown; message?: unknown };
  if (record.code === "PHONE_REQUIRED") return true;
  if (
    record.message &&
    typeof record.message === "object" &&
    (record.message as { code?: unknown }).code === "PHONE_REQUIRED"
  ) {
    return true;
  }
  return false;
};

export type ConnectorDeniedState = FeatureNotInPlanDetails;

export interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  phoneNumber?: string | null;
  isSuperAdmin: boolean;
  tosAcceptedVersion?: string;
  tosAcceptedAt?: string;
  officeRoles: {
    officeId: string;
    role: string;
    officeName: string;
    isOwner: boolean;
  }[];
}

type ProfileFetchResult = {
  profile: UserProfile | null;
  reachable: boolean;
  connectorDenied: ConnectorDeniedState | null;
};

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loadingUserData, setLoadingUserData] = useState(true);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [backendReachable, setBackendReachable] = useState(true);
  const [connectorDenied, setConnectorDenied] =
    useState<ConnectorDeniedState | null>(null);
  /** Firebase user exists but Nest refused office create without phone. */
  const [needsPhoneForRegistration, setNeedsPhoneForRegistration] =
    useState(false);

  /**
   * Fetch user profile from PG backend.
   * Returns the profile or null if the user is not registered yet.
   */
  const fetchUserProfile = useCallback(
    async (token: string): Promise<ProfileFetchResult> => {
      try {
        const res = await fetch(`${BACKEND_URL}/users/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-api-key": token,
            "x-legalix-channel": LEGALIX_CHANNEL_WEBAPP,
          },
          signal: AbortSignal.timeout(BACKEND_FETCH_TIMEOUT_MS),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const denied = parseFeatureNotInPlan(body);
          setBackendReachable(true);
          return {
            profile: null,
            reachable: true,
            connectorDenied: denied,
          };
        }
        setBackendReachable(true);
        return {
          profile: await res.json(),
          reachable: true,
          connectorDenied: null,
        };
      } catch {
        setBackendReachable(false);
        return { profile: null, reachable: false, connectorDenied: null };
      }
    },
    [],
  );

  /**
   * Ensure the user is registered in PostgreSQL with an office.
   * Called when GET /users/me returns null (new user).
   */
  const ensureRegistered = useCallback(
    async (
      firebaseUser: User,
      token: string,
      phoneNumber?: string,
    ): Promise<{
      ok: boolean;
      connectorDenied: ConnectorDeniedState | null;
      needsPhone: boolean;
    }> => {
      try {
        console.log("[useAuth] Registering user in backend...");
        const displayName =
          firebaseUser.displayName || firebaseUser.email?.split("@")[0];
        const phone =
          (phoneNumber?.trim() || readSignupPhone()).trim() || undefined;
        const postRegister = (officeName?: string) =>
          fetch(`${BACKEND_URL}/users/register`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "x-api-key": token,
              "x-legalix-channel": LEGALIX_CHANNEL_WEBAPP,
            },
            body: JSON.stringify({
              tosVersion: "1.0",
              displayName,
              ...(officeName ? { officeName } : {}),
              ...(phone ? { phoneNumber: phone } : {}),
            }),
            signal: AbortSignal.timeout(BACKEND_FETCH_TIMEOUT_MS),
          });

        let res = await postRegister();
        if (res.status === 409) {
          const taken = await res.json().catch(() => ({}));
          const suggested =
            taken?.code === "OFFICE_NAME_TAKEN" &&
            typeof taken.suggestedName === "string"
              ? taken.suggestedName
              : "";
          if (!suggested) {
            const denied = parseFeatureNotInPlan(taken);
            console.error("[useAuth] Registration failed:", res.status, denied);
            return { ok: false, connectorDenied: denied, needsPhone: false };
          }
          res = await postRegister(suggested);
        }

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (res.status === 400 && isPhoneRequiredError(body)) {
            setNeedsPhoneForRegistration(true);
            console.warn("[useAuth] Registration needs phone number");
            return { ok: false, connectorDenied: null, needsPhone: true };
          }
          const denied = parseFeatureNotInPlan(body);
          console.error("[useAuth] Registration failed:", res.status, denied);
          return { ok: false, connectorDenied: denied, needsPhone: false };
        }

        const data = await res.json();
        console.log("[useAuth] Registration result:", data);
        clearSignupPhone();
        setNeedsPhoneForRegistration(false);

        // Store officeId from registration
        if (data.office?.id) {
          localStorage.setItem("officeId", data.office.id);
        }

        // Nest writes officeId into Auth claims during register; refresh so
        // Cloud Functions see them on the next callable.
        const freshToken = await firebaseUser.getIdToken(true);
        setIdToken(freshToken);

        // Fetch fresh profile after registration
        const { profile, connectorDenied: deniedAfter } =
          await fetchUserProfile(freshToken);
        if (profile) {
          setUserProfile(profile);
          setConnectorDenied(null);
        } else if (deniedAfter) {
          setConnectorDenied(deniedAfter);
        }

        return {
          ok: Boolean(profile),
          connectorDenied: deniedAfter,
          needsPhone: false,
        };
      } catch (err) {
        console.error("[useAuth] Error during registration:", err);
        setBackendReachable(false);
        return { ok: false, connectorDenied: null, needsPhone: false };
      }
    },
    [fetchUserProfile],
  );

  const finishRegistrationWithPhone = useCallback(
    async (
      phoneNumber: string,
    ): Promise<{ ok: boolean; error: string | null }> => {
      const phone = phoneNumber.trim();
      if (!isValidSignupPhone(phone)) {
        return { ok: false, error: "invalid_phone" };
      }
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        return { ok: false, error: "no_user" };
      }
      stashSignupPhone(phone);
      try {
        const token = await firebaseUser.getIdToken();
        setIdToken(token);
        const reg = await ensureRegistered(firebaseUser, token, phone);
        if (reg.needsPhone) {
          return { ok: false, error: "phone_required" };
        }
        if (reg.connectorDenied) {
          setConnectorDenied(reg.connectorDenied);
          return { ok: false, error: "connector_denied" };
        }
        if (!reg.ok) {
          return { ok: false, error: "register_failed" };
        }
        return { ok: true, error: null };
      } catch (err) {
        console.error("[useAuth] finishRegistrationWithPhone failed:", err);
        return { ok: false, error: "register_failed" };
      }
    },
    [ensureRegistered],
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const token = await firebaseUser.getIdToken();
          setIdToken(token);
          setUser(firebaseUser);

          // Fetch user profile from PG
          const { profile, reachable, connectorDenied: denied } =
            await fetchUserProfile(token);

          if (denied) {
            setConnectorDenied(denied);
            setUserProfile(null);
            setLoadingUserData(false);
            return;
          }
          setConnectorDenied(null);

          if (profile) {
            setUserProfile(profile);

            // Set officeId if not already in localStorage
            if (!localStorage.getItem("officeId") && profile.officeRoles?.length) {
              localStorage.setItem("officeId", profile.officeRoles[0].officeId);
            }

            try {
              // /users/me backfills officeId claims when onUserCreate is off.
              setIdToken(await firebaseUser.getIdToken(true));
            } catch (refreshError) {
              console.warn("[useAuth] Failed to refresh ID token after profile load", refreshError);
            }
          }

          if (!reachable) {
            setLoadingUserData(false);
            return;
          }

          // If user has no office and is not super admin, call register
          const hasOffice = (profile?.officeRoles?.length ?? 0) > 0;
          if (!hasOffice && !profile?.isSuperAdmin) {
            const isInvitationFlow =
              window.location.pathname === "/invitation" ||
              new URLSearchParams(window.location.search)
                .get("redirect")
                ?.startsWith("/invitation");

            if (!isInvitationFlow) {
              console.log("[useAuth] User has no office — calling register");
              const reg = await ensureRegistered(firebaseUser, token);
              if (reg.connectorDenied) {
                setConnectorDenied(reg.connectorDenied);
              }
              // Keep needsPhoneForRegistration sticky until finishRegistrationWithPhone
              // succeeds; do not clear it when register returns needsPhone.
              if (reg.needsPhone) {
                setNeedsPhoneForRegistration(true);
              }
            }
          }

          setLoadingUserData(false);
        } catch (error) {
          console.error("[useAuth] Error processing auth state:", error);
          setUser(firebaseUser);
          setLoadingUserData(false);
        }
      } else {
        setUser(null);
        setUserProfile(null);
        setIdToken(null);
        setConnectorDenied(null);
        setNeedsPhoneForRegistration(false);
        localStorage.removeItem("officeId");
        setLoadingUserData(false);
      }
    });

    return unsubscribe;
  }, [ensureRegistered, fetchUserProfile]);

  const signUp = async ({
    email,
    password,
    displayName,
  }: {
    email: string;
    password: string;
    displayName?: string;
  }) => {
    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );

      if (displayName) {
        await updateProfile(result.user, { displayName });
      }

      return { user: result.user, error: null };
    } catch (error) {
      return { user: null, error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { user: result.user, error: null };
    } catch (error) {
      return { user: null, error: error as Error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      return { user: result.user, error: null };
    } catch (error) {
      return { user: null, error: error as Error };
    }
  };

  const sendVerificationEmail = async () => {
    if (!user) return { error: new Error("No user") };

    const actionCodeSettings = {
      url: `${window.location.origin}/login`,
      handleCodeInApp: false,
    };

    try {
      await sendEmailVerification(user, actionCodeSettings);
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const refreshUser = async () => {
    if (!user) return;

    try {
      await user.reload();
      const token = await user.getIdToken(true);
      setIdToken(token);

      // Re-fetch profile from PG
      const { profile, connectorDenied: denied } = await fetchUserProfile(token);
      if (denied) {
        setConnectorDenied(denied);
        setUserProfile(null);
        return;
      }
      setConnectorDenied(null);
      if (profile) {
        setUserProfile(profile);
      }
    } catch (error) {
      console.error("Error refreshing user:", error);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const updateUserProfile = async (
    displayName: string,
    photoURL: string,
  ) => {
    if (!user) return;

    try {
      await updateProfile(auth.currentUser as User, {
        displayName,
        photoURL,
      });
      await refreshUser();
    } catch (error) {
      console.error("Error updating user profile:", error);
    }
  };

  const getAccountId = (): string | undefined =>
    localStorage.getItem("officeId") ||
    userProfile?.officeRoles?.[0]?.officeId ||
    undefined;

  const isUserSuperAdmin = (): boolean =>
    userProfile?.isSuperAdmin === true;

  const isAccountAdmin = (): boolean => {
    const currentOfficeId = getAccountId();
    if (!currentOfficeId || !userProfile) return false;
    const role = userProfile.officeRoles?.find(
      (r) => r.officeId === currentOfficeId,
    );
    return role?.role === "office_admin" || role?.isOwner === true;
  };

  const hasRole = (role: string): boolean => {
    if (role === "isSuperAdmin") return isUserSuperAdmin();
    if (role === "isAccountAdmin") return isAccountAdmin();
    return false;
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  return {
    user,
    userProfile,
    loadingUserData,
    idToken,
    backendReachable,
    connectorDenied,
    needsPhoneForRegistration,
    signUp,
    signIn,
    signInWithGoogle,
    sendVerificationEmail,
    refreshUser,
    logout,
    updateUserProfile,
    hasRole,
    isUserSuperAdmin,
    isAccountAdmin,
    getAccountId,
    resetPassword,
    finishRegistrationWithPhone,
  };
};
